import React from 'react';
import { Calendar, Clock } from 'lucide-react';

export const CalendarTab = ({ calendarEvents = [], projectSettings = {}, isDark, colors }) => {
  const getEventStatus = (dateStr) => {
    const eventDate = new Date(dateStr);
    const today = new Date();
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { label: 'Past', color: isDark ? 'text-slate-500' : 'text-gray-400', bg: isDark ? 'bg-slate-500/20' : 'bg-gray-100' };
    if (diffDays === 0) return { label: 'Today', color: isDark ? 'text-green-400' : 'text-green-600', bg: isDark ? 'bg-green-500/20' : 'bg-green-50' };
    if (diffDays === 1) return { label: 'Tomorrow', color: isDark ? 'text-yellow-400' : 'text-yellow-600', bg: isDark ? 'bg-yellow-500/20' : 'bg-yellow-50' };
    if (diffDays <= 7) return { label: `In ${diffDays} days`, color: isDark ? 'text-orange-400' : 'text-orange-600', bg: isDark ? 'bg-orange-500/20' : 'bg-orange-50' };
    return { label: `In ${diffDays} days`, color: isDark ? 'text-blue-400' : 'text-blue-600', bg: isDark ? 'bg-blue-500/20' : 'bg-blue-50' };
  };

  const allEvents = [...calendarEvents];
  
  Object.entries(projectSettings).forEach(([name, settings]) => {
    if (settings.tge && settings.tge !== 'TBD') {
      const existingTGE = allEvents.find(e => e.project === name && e.type === 'tge');
      if (!existingTGE) {
        let tgeDate = null;
        const year = settings.tge.match(/\d{4}/)?.[0];
        if (year) {
          if (settings.tge.includes('Q1')) tgeDate = `${year}-03-31`;
          else if (settings.tge.includes('Q2')) tgeDate = `${year}-06-30`;
          else if (settings.tge.includes('Q3')) tgeDate = `${year}-09-30`;
          else if (settings.tge.includes('Q4')) tgeDate = `${year}-12-31`;
          else if (settings.tge.includes('-')) tgeDate = settings.tge;
        }
        if (tgeDate) {
          allEvents.push({ id: `tge-${name}`, title: `${name} - Estimated TGE`, date: tgeDate, type: 'tge', project: name });
        }
      }
    }
  });

  allEvents.sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <div className="space-y-6">
      <div className={`${colors.bgCard} border ${colors.border} rounded-xl p-6 ${colors.cardShadow}`}>
        <h3 className={`text-2xl font-bold mb-6 flex items-center gap-2 ${colors.textPrimary}`}>
          <Calendar size={28} className="text-indigo-400" />
          Calendar Events
        </h3>

        {allEvents.length === 0 ? (
          <div className={`text-center py-12 ${colors.textSecondary}`}>
            <Calendar size={48} className="mx-auto mb-4 opacity-50" />
            <p>No calendar events</p>
            <p className="text-sm mt-2">TGE events are added when you create projects</p>
          </div>
        ) : (
          <div className="space-y-3">
            {allEvents.map(event => {
              const status = getEventStatus(event.date);
              return (
                <div key={event.id} className={`${status.bg} border rounded-lg p-4 flex items-center justify-between ${isDark ? 'border-slate-600' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-4">
                    <div className="text-center min-w-[60px]">
                      <div className={`text-2xl font-bold ${colors.textPrimary}`}>{new Date(event.date).getDate()}</div>
                      <div className={`text-xs uppercase ${colors.textSecondary}`}>{new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}</div>
                    </div>
                    <div>
                      <div className={`font-medium ${colors.textPrimary}`}>{event.title}</div>
                      <div className={`text-sm flex items-center gap-1 ${colors.textSecondary}`}>
                        <Clock size={14} />
                        {event.type === 'tge' ? 'Estimated TGE' : 'Event'}
                      </div>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${status.color} ${status.bg}`}>{status.label}</div>
                </div>
              );
            })}
          </div>
        )}

        <div className={`mt-6 pt-4 border-t ${colors.border}`}>
          <div className={`text-sm mb-2 ${colors.textSecondary}`}>Legend:</div>
          <div className="flex flex-wrap gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className={colors.textSecondary}>Upcoming</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <span className={colors.textSecondary}>Less than 7 days</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${isDark ? 'bg-slate-500' : 'bg-gray-400'}`}></div>
              <span className={colors.textSecondary}>Past</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};